import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { useRoutePageviews } from '@core/hooks/useAnalytics';
import LoadingState from '@components/base/LoadingState';
import DefaultLayout from '@/layouts/DefaultLayout.jsx';
import ProtectedRoute from './ProtectedRoute.jsx';

const Home = lazy(() => import('@features/catalog/HomePage.jsx'));
const Catalog = lazy(() => import('@features/catalog/CatalogPage.jsx'));
const Categories = lazy(() => import('@features/catalog/CategoriesPage.jsx'));
const ProductDetail = lazy(() => import('@features/product-detail/ProductDetailPage.jsx'));
const Cart = lazy(() => import('@features/cart/CartPage.jsx'));
const Wishlist = lazy(() => import('@features/wishlist/WishlistPage.jsx'));
const Checkout = lazy(() => import('@features/checkout/CheckoutPage.jsx'));
const CashfreePayment = lazy(() => import('@features/checkout/CashfreePaymentPage.jsx'));
const RazorpayPayment = lazy(() => import('@features/checkout/RazorpayPaymentPage.jsx'));
const StripePayment = lazy(() => import('@features/checkout/StripePaymentPage.jsx'));
const PaymentReturn = lazy(() => import('@features/checkout/PaymentReturnPage.jsx'));
const Login = lazy(() => import('@features/auth/LoginPage.jsx'));
const Signup = lazy(() => import('@features/auth/SignupPage.jsx'));
const VerifyOtp = lazy(() => import('@features/auth/VerifyOtpPage.jsx'));
const ForgotPassword = lazy(() => import('@features/auth/ForgotPasswordPage.jsx'));
const Account = lazy(() => import('@features/account/AccountPage.jsx'));
const ChangePassword = lazy(() => import('@features/account/ChangePasswordPage.jsx'));
const MyOrders = lazy(() => import('@features/account/MyOrdersPage.jsx'));
const OrderDetail = lazy(() => import('@features/account/OrderDetailPage.jsx'));
const Terms = lazy(() => import('@features/legal/TermsPage.jsx'));
const Privacy = lazy(() => import('@features/legal/PrivacyPage.jsx'));
const AboutUs = lazy(() => import('@features/legal/AboutUsPage.jsx'));
const FAQ = lazy(() => import('@features/legal/FAQPage.jsx'));
const NotFound = lazy(() => import('./NotFound.jsx'));
const routeLoaderDurationMs = 1200;

// Blank, full-height placeholder while a route chunk loads. It holds the
// RouteLoaderOverlay open (onPending) so the page renders blurred *behind*
// the loader instead of a bare loader on an empty screen.
function PageFallback({ onPending }) {
  useEffect(() => {
    onPending(true);
    return () => onPending(false);
  }, [onPending]);
  return <div style={{ minHeight: '100vh' }} />;
}

function RouteLoaderOverlay() {
  return (
    <div
      className="route-loader-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(255, 255, 255, 0.88)',
        backdropFilter: 'blur(0.5rem)',
        WebkitBackdropFilter: 'blur(0.5rem)',
      }}
    >
      <LoadingState minHeight={0} size="xlarge" className="loading-state--page" />
    </div>
  );
}

const protect = (el) => <ProtectedRoute>{el}</ProtectedRoute>;
function RouteParamSync() {
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    let update = false;
    const next = new URLSearchParams(params);

    if (next.has('account')) {
      next.delete('account');
      update = true;
    }

    if (next.has('location')) {
      next.delete('location');
      update = true;
    }

    if (update) setParams(next, { replace: true });
  }, [params, setParams]);

  return null;
}

export default function AppRoutes() {
  const location = useLocation();
  const previousPathnameRef = useRef(null);
  const [isRouteLoading, setIsRouteLoading] = useState(true);
  const [isProductDetailModalOpen, setIsProductDetailModalOpen] = useState(false);
  const [isChunkPending, setIsChunkPending] = useState(false);

  useRoutePageviews();

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    // Opening / closing the product modal (/products/:id) is not a page change:
    // the full-screen loader flashed over the page as the modal closed.
    const isProductModalHop =
      previousPathname?.startsWith('/products/') || location.pathname.startsWith('/products/');

    if (previousPathname === location.pathname || isProductModalHop) {
      setIsRouteLoading(false);
      return undefined;
    }

    setIsRouteLoading(true);
    const timer = window.setTimeout(() => {
      setIsRouteLoading(false);
    }, routeLoaderDurationMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.key, location.pathname]);

  useEffect(() => {
    const onProductDetailModalChange = (event) => {
      setIsProductDetailModalOpen(Boolean(event.detail?.open));
    };

    window.addEventListener('product-detail-modal-change', onProductDetailModalChange);
    return () => {
      window.removeEventListener('product-detail-modal-change', onProductDetailModalChange);
    };
  }, []);

  return (
    <>
      <RouteParamSync />
      <Suspense fallback={<PageFallback onPending={setIsChunkPending} />}>
        <Routes>
          {/* Auth pages render bare via AuthLayout — no storefront chrome */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Storefront pages share the default layout (variant-resolved Header) */}
          <Route element={<DefaultLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment/cashfree" element={<CashfreePayment />} />
            <Route path="/payment/razorpay" element={<RazorpayPayment />} />
            <Route path="/payment/stripe" element={<StripePayment />} />
            <Route path="/payment/stripe/:appOrderId" element={<StripePayment />} />
            <Route path="/payment/return" element={<PaymentReturn />} />
            <Route path="/account" element={protect(<Account />)} />
            <Route path="/account/change-password" element={protect(<ChangePassword />)} />
            <Route path="/orders" element={protect(<MyOrders />)} />
            <Route path="/orders/:id" element={protect(<OrderDetail />)} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/faqs" element={<FAQ />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {(isRouteLoading || isChunkPending) && !isProductDetailModalOpen ? (
        <RouteLoaderOverlay />
      ) : null}
    </>
  );
}
